import * as Sentry from '@sentry/react';
import { lazy } from 'react';
import { RootLayout } from '../layouts/root-layout';
import { SubRoutingLayout } from '../layouts/sub-routing-layout';
import { createBrowserRouter, Navigate } from 'react-router-dom';

const ErrorPage = lazy(() =>
	import('@pages/ErrorPage').then((module) => ({
		default: module.ErrorPage,
	}))
);

const LoginPage = lazy(() =>
	import('@pages/LoginPage').then((module) => ({
		default: module.LoginPage,
	}))
);

const SSoLoginPage = lazy(() =>
	import('@pages/SSoLoginPage').then((module) => ({
		default: module.SSoLoginPage,
	}))
);

const Support = lazy(() =>
	import('@pages/supportpage').then((module) => ({
		default: module.Support,
	}))
);

const EndUsers = lazy(() =>
	import('@components/member/components').then((module) => ({
		default: module.EndUsers,
	}))
);

const Leads = lazy(() =>
	import('@components/member/components').then((module) => ({
		default: module.Leads,
	}))
);

const BasicDetails = lazy(() =>
	import('@components/endUserForm/components/bacisDetails/BasicDetails').then((module) => ({
		default: module.BasicDetails,
	}))
);

const MoreDetails = lazy(() =>
	import('@components/endUserForm/components/moreDetails/moreDetails').then((module) => ({
		default: module.MoreDetails,
	}))
);

const AllMeetingList = lazy(() =>
	import('@components/meeting/components/AllMeetingList/AllMeetingList').then((module) => ({
		default: module.AllMeetingList,
	}))
);

const ScheduledMeetingList = lazy(() =>
	import('@components/meeting/components/ScheduledMeetingList/ScheduledMeetingList').then((module) => ({
		default: module.ScheduledMeetingList,
	}))
);

const TodayMeetingList = lazy(() =>
	import('@components/meeting/components/TodayMeetingList/TodayMeetingList').then((module) => ({
		default: module.TodayMeetingList,
	}))
);

const DocumentUpload = lazy(() =>
	import('@components/identityVerification/components/documentUpload/documentUpload').then((module) => ({
		default: module.DocumentUpload,
	}))
);

const TakeSelfie = lazy(() =>
	import('@components/identityVerification/components/takeSelfie/takeSelfie').then((module) => ({
		default: module.TakeSelfie,
	}))
);

const AddMeetingDetails = lazy(() =>
	import('@components/addNewMeeting/components/addMeetingDetails/addMeetingDetails').then((module) => ({
		default: module.AddMeetingDetails,
	}))
);

const AddMeetingParticipants = lazy(() =>
	import('@components/addNewMeeting/components/addMeetingParticipants/addMeetingParticipants').then((module) => ({
		default: module.AddMeetingParticipants,
	}))
);

const AddNewSiteLayout = lazy(() =>
	import('@components/addNewSite/addNewSiteLayout').then((module) => ({
		default: module.AddNewSiteLayout,
	}))
);

const BasicDetailsNewSite = lazy(() =>
	import('@components/addNewSite/components/basicDetailsNewSite/basicDetailsNewSite').then((module) => ({
		default: module.BasicDetailsNewSite,
	}))
);

const SurfaceDetails = lazy(() =>
	import('@components/addNewSite/components/surfaceDetails/surfaceDetails').then((module) => ({
		default: module.SurfaceDetails,
	}))
);

const QuestionnarieDetails = lazy(() =>
	import('@components/generatedDr/components/questionnarieDetails/questionnarieDetails').then((module) => ({
		default: module.QuestionnarieDetails,
	}))
);

const ProductSamplingListLayout = lazy(() =>
	import('@components/productSampling/productSamplingListLayout').then((module) => ({
		default: module.ProductSamplingListLayout,
	}))
);

const RaiseSamplingRequest = lazy(() =>
	import('@components/productSampling/components/raiseSamplingRequest/raiseSamplingRequest').then((module) => ({
		default: module.RaiseSamplingRequest,
	}))
);

const RaincoatPreview = lazy(() =>
	import('@components/productSampling/components/raincoatPreview/RaincoatPreview').then((module) => ({
		default: module.RaincoatPreview,
	}))
);

const SamplingRequest = lazy(() =>
	import('@components/productSampling/components/samplingRequest/SamplingRequest').then((module) => ({
		default: module.SamplingRequest,
	}))
);

const EndUserDetails = lazy(() =>
	import('@pages/EndUserDetails').then((module) => ({
		default: module.EndUserDetails,
	}))
);

const Dashboard = lazy(() =>
	import('@pages/DashboardPage').then((module) => ({
		default: module.DashboardPage,
	}))
);

const PCMSPage = lazy(() =>
	import('@pages/PcmsPage').then((module) => ({
		default: module.PcmsPage,
	}))
);

const JharokhaPage = lazy(() =>
	import('@pages/JharokhaPage').then((module) => ({
		default: module.jharokhaPage,
	}))
);

const Profile = lazy(() =>
	import('@pages/ProfilePage').then((module) => ({
		default: module.ProfilePage,
	}))
);

const MemberPage = lazy(() =>
	import('@pages/MemberPage').then((module) => ({
		default: module.MemberPage,
	}))
);

const User360Page = lazy(() =>
	import('@pages/User360Page').then((module) => ({
		default: module.User360Page,
	}))
);

const KYCVerificationPage = lazy(() =>
	import('@pages/KYCVerification').then((module) => ({
		default: module.KYCVerification,
	}))
);

const BankDetailsPage = lazy(() =>
	import('@pages/BankDetailsPage').then((module) => ({
		default: module.BankDetailsPage,
	}))
);

const IdentityVerification = lazy(() =>
	import('@pages/IdentityVerification').then((module) => ({
		default: module.IdentityVerification,
	}))
);

const MeetingListPage = lazy(() =>
	import('@pages/MeetingListPage').then((module) => ({
		default: module.MeetingListPage,
	}))
);

const MeetingDetailsPage = lazy(() =>
	import('@pages/MeetingDetailsPage').then((module) => ({
		default: module.MeetingDetailsPage,
	}))
);

const AddNewMeeting = lazy(() =>
	import('@pages/AddNewMeeting').then((module) => ({
		default: module.AddNewMeeting,
	}))
);

const OneToOneMeetingPage = lazy(() =>
	import('@pages/OneToOneMeeting').then((module) => ({
		default: module.OneToOneMeetingPage,
	}))
);

const DfccPage = lazy(() =>
	import('@pages/DfccPage').then((module) => ({
		default: module.DfccPage,
	}))
);

const DfccDetailsPage = lazy(() =>
	import('@pages/DfccDetailsPage').then((module) => ({
		default: module.DfccDetailsPage,
	}))
);

const TransferMemberPage = lazy(() =>
	import('@pages/TransferMemberPage').then((module) => ({
		default: module.TransferMemberPage,
	}))
);

const SiteListPage = lazy(() =>
	import('@pages/SiteListPage').then((module) => ({
		default: module.SiteListPage,
	}))
);

const CalendarPage = lazy(() =>
	import('@pages/CalendarPage').then((module) => ({
		default: module.CalendarPage,
	}))
);

const QrCodeScannerPage = lazy(() =>
	import('@pages/QrCodeScanner').then((module) => ({
		default: module.QrCodeScanner,
	}))
);

const QrCodeCheck = lazy(() =>
	import('@components/qrCodeScanner/components/qrCodeCheck/qrCodeCheck').then((module) => ({
		default: module.QrCodeCheck,
	}))
);

const QrCodeValidation = lazy(() =>
	import('@components/qrCodeScanner/components/qrCodeValidation/qrCodeValidation').then((module) => ({
		default: module.QrCodeValidation,
	}))
);

const RecommendationReportPage = lazy(() =>
	import('@pages/RecommendationReportPage').then((module) => ({
		default: module.RecommendationReportPage,
	}))
);

const CompetitionPage = lazy(() =>
	import('@pages/CompetitionPage').then((module) => ({
		default: module.CompetitionPage,
	}))
);

const RaincoatPreviewPage = lazy(() =>
	import('@pages/AddRainCoatPreview').then((module) => ({
		default: module.AddRaincoatPreview,
	}))
);

const StructureQuestionnairePage = lazy(() =>
	import('@pages/StructureQuestionnairePage').then((module) => ({
		default: module.StructureQuestionnairePage,
	}))
);

const DetailsSummaryMonth = lazy(() =>
	import('@components/dashboardDetailsSummary/detailsSummaryMonth/detailsSummaryMonth').then((module) => ({
		default: module.DetailsSummaryMonth,
	}))
);

const DetailsSummaryQuarter = lazy(() =>
	import('@components/dashboardDetailsSummary/detailsSummaryQuarter/detailsSummaryQuarter').then((module) => ({
		default: module.DetailsSummaryQuarter,
	}))
);

const DetailsSummaryYear = lazy(() =>
	import('@components/dashboardDetailsSummary/detailsSummaryYear/detailsSummaryYear').then((module) => ({
		default: module.DetailsSummaryYear,
	}))
);

const DashboardDetailsSummaryLayout = lazy(() =>
	import('@components/dashboardDetailsSummary/dashboardDetailsSummaryLayout').then((module) => ({
		default: module.DashboardDetailsSummaryLayout,
	}))
);

const DashboardKaencDetailsPage = lazy(() =>
	import('@pages/DashboardKaencDetailsPage').then((module) => ({
		default: module.DashboardKaencDetailsPage,
	}))
);

const DashboardKaencPage = lazy(() =>
	import('@pages/DashboardKaencPage').then((module) => ({
		default: module.DashboardKaencPage,
	}))
);

const CaseModulePage = lazy(() =>
	import('@pages/CaseModulePage').then((module) => ({
		default: module.CaseModulePage,
	}))
);

const CaseModuleDetailsPage = lazy(() =>
	import('@pages/CaseModuleDetailsPage').then((module) => ({
		default: module.CaseModuleDetailsPage,
	}))
);

const UserTransferPage = lazy(() =>
	import('@pages/UserTransferPage').then((module) => ({
		default: module.UserTransferPage,
	}))
);

const UserTransferRequestList = lazy(() =>
	import('@components/userTransfer/components/userTransferRequestList/UserTransferRequestList').then((module) => ({
		default: module.UserTransferRequestList,
	}))
);

const UserTransferApprovedList = lazy(() =>
	import('@components/userTransfer/components/userTransferApprovedList/UserTransferApprovedList').then((module) => ({
		default: module.UserTransferApprovedList,
	}))
);

const UserTransferRejectedList = lazy(() =>
	import('@components/userTransfer/components/userTransferRejectedList/UserTransferRejectedList').then((module) => ({
		default: module.UserTransferRejectedList,
	}))
);

const ProductDiscussionDemo = lazy(() =>
	import('@pages/ProductDiscussionDemo').then((module) => ({
		default: module.ProductDiscussionDemo,
	}))
);

const RedemptionBuilding = lazy(() =>
	import('@pages/RedemptionBuilding').then((module) => ({
		default: module.RedemptionBuilding,
	}))
);

const MarketFeedback = lazy(() =>
	import('@pages/MarketFeedback').then((module) => ({
		default: module.MarketFeedback,
	}))
);

const LeadGeneration = lazy(() =>
	import('@pages/LeadGeneration').then((module) => ({
		default: module.LeadGeneration,
	}))
);

const SchemaDiscussion = lazy(() =>
	import('@pages/SchemaDiscussion').then((module) => ({
		default: module.SchemaDiscussion,
	}))
);

const ComplaintHandling = lazy(() =>
	import('@pages/ComplaintHandling').then((module) => ({
		default: module.ComplaintHandling,
	}))
);

const ProductRecommendation = lazy(() =>
	import('@pages/ProductRecommendation').then((module) => ({
		default: module.ProductRecommendation,
	}))
);

const OneToOneMeetingLayout = lazy(() =>
	import('@components/meetingStackholder/oneToOneMeetingLayout').then((module) => ({
		default: module.OneToOneMeetingLayout,
	}))
);

const StackholderMappingPage = lazy(() =>
	import('@pages/StackholderMappingPage').then((module) => ({
		default: module.StackholderMappingPage,
	}))
);

const SiteDetailsRRPage = lazy(() =>
	import('@pages/SiteDetailsRRPage').then((module) => ({
		default: module.SiteDetailsRRPage,
	}))
);

const PointDashboard = lazy(() =>
	import('@pages/PointDashboard').then((module) => ({
		default: module.PointDashboard,
	}))
);

const PointsHistory = lazy(() =>
	import('@pages/PointsHistory').then((module) => ({
		default: module.PointsHistory,
	}))
);

const BankingHistory = lazy(() =>
	import('@pages/BankingHistory').then((module) => ({
		default: module.BankingHistory,
	}))
);

const RedemptionHistory = lazy(() =>
	import('@pages/RedemptionHistory').then((module) => ({
		default: module.RedemptionHistory,
	}))
);

const PhysicalVerificationPage = lazy(() =>
	import('@pages/PhysicalVerificationPage').then((module) => ({
		default: module.PhysicalVerificationPage,
	}))
);

const ProspectiveSitePage = lazy(() =>
	import('@pages/ProspectiveSitePage').then((module) => ({
		default: module.ProspectiveSitePage,
	}))
);

const SiteLogVisitPage = lazy(() =>
	import('@pages/SiteLogVisitPage').then((module) => ({
		default: module.SiteLogVisitPage,
	}))
);

const AddProspectiveSite = lazy(() =>
	import('@pages/AddProspectiveSite').then((module) => ({
		default: module.AddProspectiveSite,
	}))
);

const SendFeedbackPage = lazy(() =>
	import('@pages/SendFeedbackPage').then((module) => ({
		default: module.SendFeedbackPage,
	}))
);

const SendExpenseDetailsPage = lazy(() =>
	import('@pages/SendExpenseDetailsPage').then((module) => ({
		default: module.SendExpenseDetailsPage,
	}))
);

const CompleteMeetingPage = lazy(() =>
	import('@pages/CompleteMeetingPage').then((module) => ({
		default: module.CompleteMeetingPage,
	}))
);

const CompleteMeetingDetailsPage = lazy(() =>
	import('@pages/CompleteMeetingDetailsPage').then((module) => ({
		default: module.CompleteMeetingDetailsPage,
	}))
);

const SelectClubMemberPage = lazy(() =>
	import('@pages/SelectClubMember').then((module) => ({
		default: module.SelectClubMemberPage,
	}))
);

const GeneratedDrReport = lazy(() =>
	import('@pages/GeneratedDrReport').then((module) => ({
		default: module.GeneratedDrReport,
	}))
);

const GeneratedDrPage = lazy(() =>
	import('@pages/GeneratedDrPage').then((module) => ({
		default: module.GeneratedDrPage,
	}))
);

const BusinessGeneratedPage = lazy(() =>
	import('@pages/BusinessGeneratedPage').then((module) => ({
		default: module.BusinessGeneratedPage,
	}))
);
const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);

export const router = sentryCreateBrowserRouter([
	{
		path: '/login',
		errorElement: <ErrorPage />,
		children: [
			{
				element: <LoginPage />,
				index: true,
			},
		],
	},
	{
		path: 'sso-login/',
		errorElement: <ErrorPage />,
		children: [
			{
				element: <SSoLoginPage />,
				index: true,
			},
		],
	},
	{
		path: '/jharokha',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/jharokha',
				element: <JharokhaPage />,
			},
		],
	},
	{
		path: '/profile',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				index: true,
				element: <Profile />,
			},
		],
	},
	{
		path: '/',
		element: <RootLayout />,
		errorElement: <ErrorPage />,
		children: [
			{
				index: true,
				element: <Dashboard />,
			},
			{
				path: '/end-users',
				element: <MemberPage />,
				children: [
					{
						index: true,
						element: <EndUsers />,
					},
				],
			},
			{
				path: '/leads',
				element: <MemberPage />,
				children: [
					{
						index: true,
						element: <Leads />,
					},
				],
			},
			{
				path: '/meetings',
				element: <MeetingListPage />,
				handle: { title: 'Meetings' },
				children: [
					{
						index: true,
						element: <ScheduledMeetingList />,
						handle: { title: 'Meetings' },
					},
					{
						path: 'todays-meetings',
						element: <TodayMeetingList />,
						handle: { title: 'Meetings' },
					},
					{
						path: 'all-meetings',
						handle: { title: 'Meetings' },
						element: <AllMeetingList />,
					},
				],
			},
			{
				path: '/prospective-site',
				element: <ProspectiveSitePage />,
			},
		],
	},
	{
		path: '/',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				index: true,
				element: <Dashboard />,
				handle: { title: 'Dashboard' },
			},
			{
				path: '/pcms',
				element: <PCMSPage />,
			},
			{
				path: '/help-and-support',
				element: <Support />,
			},
			{
				path: '/contractor',
				handle: { title: 'Contractor Overview' },
				element: <User360Page />,
			},
			{
				path: '/contractor/competition-details',
				element: <CompetitionPage />,
			},
			{
				path: '/kyc-verification',
				handle: { title: 'Identity Verification' },
				element: <KYCVerificationPage />,
			},
			{
				path: '/bank-details',
				element: <BankDetailsPage />,
				handle: { title: 'Bank KYC' },
			},
			{
				path: '/identity-verification',
				element: <IdentityVerification />,
				children: [
					{
						index: true,
						element: <Navigate to="/identity-verification/upload-document" replace />,
						handle: { title: 'Identity Verification' },
					},
					{
						path: 'upload-document',
						element: <DocumentUpload />,
					},
					{
						path: 'selfie-verification',
						element: <TakeSelfie />,
					},
				],
			},
			{
				path: '/meeting-details',
				element: <MeetingDetailsPage />,
				handle: { title: 'Meeting' },
			},
			{
				path: '/today-meetings',
				element: <MeetingDetailsPage />,
				handle: { title: 'Meeting' },
			},
		],
	},

	{
		path: '/lead',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/lead',
				element: <EndUserDetails />,
				children: [
					{
						index: true,
						element: <Navigate to="basic-details" replace />,
					},
					{
						path: 'basic-details',
						element: <BasicDetails />,
					},
					{
						path: 'more-details',
						element: <MoreDetails />,
					},
				],
			},
		],
	},
	{
		path: '/end-user',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/end-user',
				element: <EndUserDetails />,
				children: [
					{
						index: true,
						element: <Navigate to="basic-details" replace />,
					},
					{
						path: 'basic-details',
						element: <BasicDetails />,
					},
					{
						path: 'more-details',
						element: <MoreDetails />,
					},
				],
			},
		],
	},
	{
		path: '/contractor',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				index: true,
				element: <User360Page />,
				handle: { title: 'Contractor Overview' },
			},
			{
				path: '/contractor/qr-code-scanner',
				element: <QrCodeScannerPage />,
			},
			{
				path: '/contractor/qr-code-check',
				element: <QrCodeCheck />,
			},
			{
				path: '/contractor/qr-code-validate',
				element: <QrCodeValidation />,
			},
			{
				path: '/contractor/banking-history',
				element: <BankingHistory />,
			},
			{
				path: '/contractor/redemption-history',
				element: <RedemptionHistory />,
			},
			{
				path: '/contractor/points-statement',
				element: <PointsHistory />,
			},
			{
				path: '/contractor/points-dashboard',
				element: <PointDashboard />,
			},
			{
				path: '/contractor/physical-verification',
				element: <PhysicalVerificationPage />,
			},
		],
	},
	{
		path: '/one-to-one-meeting',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/one-to-one-meeting',
				element: <OneToOneMeetingPage />,
				handle: { title: 'One to One Meeting' },
			},
		],
	},
	{
		path: '/meeting-details',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/meeting-details',
				element: <MeetingDetailsPage />,
			},
		],
	},
	{
		path: '/one-to-one-meeting/add',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/one-to-one-meeting/add',
				element: <OneToOneMeetingLayout />,
				handle: { title: 'One to One Meeting Details' },
			},
		],
	},
	{
		path: '/one-to-one-meeting/edit',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/one-to-one-meeting/edit',
				element: <OneToOneMeetingLayout />,
				handle: { title: 'One to One Meeting Details' },
			},
		],
	},
	{
		path: '/product-discussion-demo',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/product-discussion-demo',
				element: <ProductDiscussionDemo />,
			},
		],
	},
	{
		path: '/scheme-discussion',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/scheme-discussion',
				element: <SchemaDiscussion />,
			},
		],
	},
	{
		path: '/complaint-handling',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/complaint-handling',
				element: <ComplaintHandling />,
			},
		],
	},
	{
		path: '/relationship-building',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/relationship-building',
				element: <RedemptionBuilding />,
			},
		],
	},
	{
		path: '/market-feedback',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/market-feedback',
				element: <MarketFeedback />,
			},
		],
	},
	{
		path: '/lead-generation',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/lead-generation',
				element: <LeadGeneration />,
			},
		],
	},
	{
		path: '/product-recommendation',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/product-recommendation',
				element: <ProductRecommendation />,
			},
		],
	},
	{
		path: '/meeting',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		handle: { title: 'Meeting' },
		children: [
			{
				path: '/meeting',
				element: <AddNewMeeting />,
				handle: { title: 'Meeting' },
				children: [
					{
						index: true,
						element: <Navigate to="/meeting/meeting-details" replace />,
						handle: { title: 'Meeting' },
					},
					{
						path: 'meeting-details',
						element: <AddMeetingDetails />,
						handle: { title: 'Meeting' },
					},
					{
						path: 'meeting-participants',
						element: <AddMeetingParticipants />,
						handle: { title: 'Meeting Participants' },
					},
				],
			},
		],
	},
	{
		path: '/send-feedback',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/send-feedback',
				element: <SendFeedbackPage />,
			},
		],
	},
	{
		path: '/send-expense-details',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/send-expense-details',
				element: <SendExpenseDetailsPage />,
			},
		],
	},
	{
		path: '/complete-meeting',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/complete-meeting',
				element: <CompleteMeetingPage />,
			},
		],
	},

	{
		path: '/complete-meeting-details',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/complete-meeting-details',
				element: <CompleteMeetingDetailsPage />,
			},
		],
	},

	{
		path: '/case-module',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/case-module',
				element: <CaseModulePage />,
			},
		],
	},

	{
		path: '/case-module-details',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/case-module-details',
				element: <CaseModuleDetailsPage />,
			},
		],
	},

	{
		path: '/user-transfer',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/user-transfer',
				element: <UserTransferPage />,
				children: [
					{
						index: true,
						element: <Navigate to="/user-transfer/request" replace />,
					},
					{
						path: 'request',
						element: <UserTransferRequestList />,
					},
					{
						path: 'approved',
						element: <UserTransferApprovedList />,
					},
					{
						path: 'rejected',
						element: <UserTransferRejectedList />,
					},
				],
			},
		],
	},

	{
		path: '/calendar',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				index: true,
				element: <CalendarPage />,
			},
		],
	},

	{
		path: '/add-prospective-site',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/add-prospective-site',
				element: <AddProspectiveSite />,
			},
		],
	},

	{
		path: '/add-new-site',
		errorElement: <ErrorPage />,
		handle: { title: 'Add New Site' },
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/add-new-site',
				element: <AddNewSiteLayout />,
				children: [
					{
						index: true,
						element: <Navigate to="/add-new-site/basic" replace />,
					},
					{
						path: 'basic',
						element: <BasicDetailsNewSite />,
						handle: { title: 'Add New Site' },
					},
					{
						path: 'surface',
						element: <SurfaceDetails />,
						handle: { title: 'Add New Site' },
					},
				],
			},
		],
	},
	{
		path: '/sites',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				index: true,
				element: <SiteListPage />,
			},
		],
	},
	{
		path: '/site-details-rr',
		errorElement: <ErrorPage />,
		handle: { title: 'Site 360' },
		element: <SubRoutingLayout />,
		children: [
			{
				index: true,
				element: <SiteDetailsRRPage />,
			},
		],
	},
	{
		path: '/stakeholder-mapping',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/stakeholder-mapping',
				element: <StackholderMappingPage />,
				handle: { title: 'Stakeholder Mapping' },
			},
		],
	},
	{
		path: '/dfcc-clubs',
		errorElement: <ErrorPage />,
		handle: { title: 'DFCC Clubs' },
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/dfcc-clubs',
				element: <DfccPage />,
				children: [
					{
						index: true,
						element: <DfccPage />,
					},
				],
			},
		],
	},
	{
		path: '/dfcc-clubs/club-details',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				index: true,
				element: <DfccDetailsPage />,
				handle: { title: 'DFCC Clubs' },
			},
		],
	},
	{
		path: '/dfcc-clubs/transfer-member',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				index: true,
				element: <TransferMemberPage />,
				handle: { title: 'Transfer Member' },
			},
		],
	},
	{
		path: '/dfcc-clubs/select-club-member',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				index: true,
				element: <SelectClubMemberPage />,
				handle: { title: 'Select Club' },
			},
		],
	},
	{
		path: '/site-log-visit',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/site-log-visit',
				element: <SiteLogVisitPage />,
			},
		],
	},
	{
		path: '/generated-dr-report',
		errorElement: <ErrorPage />,
		handle: { title: 'Generate DR Report' },
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/generated-dr-report',
				element: <GeneratedDrReport />,
			},
		],
	},
	{
		path: '/generated-dr',
		errorElement: <ErrorPage />,
		handle: { title: 'Generate DR Report' },
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/generated-dr',
				element: <GeneratedDrPage />,
				children: [
					{
						index: true,
						element: <QuestionnarieDetails />,
					},
				],
			},
		],
	},
	{
		path: '/recommendation-report',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				element: <RecommendationReportPage />,
				index: true,
			},
		],
	},
	{
		path: '/dashboard-detailed-summary',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		handle: { title: 'Detailed Summary' },
		children: [
			{
				path: '/dashboard-detailed-summary',
				element: <DashboardDetailsSummaryLayout />,
				handle: { title: 'Detailed Summary' },
				children: [
					{
						index: true,
						element: <Navigate to="month" replace />,
					},
					{
						path: 'month',
						element: <DetailsSummaryMonth />,
						handle: { title: 'Detailed Summary' },
					},
					{
						path: 'quarter',
						element: <DetailsSummaryQuarter />,
						handle: { title: 'Detailed Summary' },
					},
					{
						path: 'year',
						element: <DetailsSummaryYear />,
						handle: { title: 'Detailed Summary' },
					},
				],
			},
		],
	},
	{
		path: '/product-sampling-add',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/product-sampling-add',
				element: <RaiseSamplingRequest />,
			},
		],
	},
	{
		path: '/product-sampling-detail',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/product-sampling-detail',
				element: <RaiseSamplingRequest />,
			},
		],
	},
	{
		path: '/raincoat-preview-add',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/raincoat-preview-add',
				element: <RaincoatPreviewPage />,
			},
		],
	},
	{
		path: '/raincoat-preview-detail',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/raincoat-preview-detail',
				element: <RaincoatPreviewPage />,
			},
		],
	},
	{
		path: '/product-sampling-list',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/product-sampling-list',
				element: <ProductSamplingListLayout />,
				children: [
					{
						index: true,
						element: <SamplingRequest />,
						handle: { title: 'Product Sampling' },
					},
					{
						path: 'raincoat-preview',
						element: <RaincoatPreview />,
						handle: { title: 'Raincoat Preview' },
					},
				],
			},
		],
	},
	{
		path: '/business-generated',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/business-generated',
				element: <BusinessGeneratedPage />,
			},
		],
	},

	{
		path: '/dashboard-kaenc',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/dashboard-kaenc',
				element: <DashboardKaencPage />,
			},
		],
	},

	{
		path: '/dashboard-kaenc-details',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		handle: { title: 'Detailed Summary' },
		children: [
			{
				path: '/dashboard-kaenc-details',
				element: <DashboardKaencDetailsPage />,
				handle: { title: 'Detailed Summary' },
			},
		],
	},
	{
		path: '/feedback-questionnaires',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/feedback-questionnaires',
				element: <StructureQuestionnairePage />,
			},
		],
	},
]);
